import { use } from "react";
import { ClientProfile, ProfileType } from "../__generated__/graphql";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { AuthManagerContext } from "../contexts/AuthManager";
import { useCurrentProfile } from "./use-current-profile";
import { useI18n } from "./i18n-hooks";
import { ScreenCardHeadbandProps } from "../screens/ScreenCardHeadband";

/**
 * Warning headband nagging an employer to verify their email. Meant for the main-nav root
 * screens; returns `undefined` (no headband) unless the active profile is a client whose email is
 * not yet verified. Worker profiles are excluded. Company employers verify a distinct business
 * email (`businessEmailVerified`); private (domestic) employers reuse their personal email, so
 * their verification state is the user's (`user.verified`) — mirroring how ProfileScreen surfaces
 * each.
 */
export const useBusinessEmailVerificationHeadband = (): ScreenCardHeadbandProps | undefined => {
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { user } = use(AuthManagerContext);
    const clientProfile = useCurrentProfile<ClientProfile>();

    if (profileType !== ProfileType.Client) {
        return undefined;
    }

    const unverified = clientProfile?.isPrivateClient
        ? !user?.verified
        : !!clientProfile?.businessEmail && !clientProfile.businessEmailVerified;

    if (!unverified) {
        return undefined;
    }

    return {
        mode: "warning",
        text: i18n.t("user.business_email_verification_banner"),
    };
};
